import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from "react-router-dom";
import { LocalEnvironmentName } from "waapi/environments-repository/constants/environments.js";
import { getCurrentMystiqueEnv } from "../../utils/getCurrentMystiqueEnv.js";
import { isChunkLoadError, isDynamicImportError } from "../../utils/handleErrorBondary.js";

let sentryReplay: ReturnType<typeof Sentry.replayIntegration> | null = null;

export const getSentryReplay = () => sentryReplay;

export const initSentry = () => {
    if (sentryReplay) return;
    const { VITE_SENTRY_DSN } = process.env;
    const mystiqueEnv = getCurrentMystiqueEnv();
    if (mystiqueEnv === LocalEnvironmentName.LOCAL || process.env.STORYBOOK === "true" || process.env.NODE_ENV === "test" || !VITE_SENTRY_DSN) {
        console.warn(
            `Sentry not initialized because mystique env is '${mystiqueEnv}': local or storybook or test or no VITE_SENTRY_DSN was provided`,
        );
        return;
    }
    // eslint-disable-next-line no-console
    console.info(`Sentry initialized for env ${mystiqueEnv}`);

    try {
        sentryReplay = Sentry.replayIntegration({
            maskAllText: false,
            maskAllInputs: false,
            blockAllMedia: false,
        });

        Sentry.init({
            dsn: VITE_SENTRY_DSN,
            environment: mystiqueEnv === "prod" ? "production" : mystiqueEnv,
            beforeSend(event, hint) {
                if (
                    hint &&
                    hint.originalException instanceof Error &&
                    (isChunkLoadError(hint.originalException) || isDynamicImportError(hint.originalException))
                ) {
                    return null;
                }
                return event;
            },
            integrations: [
                Sentry.browserTracingIntegration({}),
                Sentry.reactRouterV6BrowserTracingIntegration({
                    useEffect,
                    useLocation,
                    useNavigationType,
                    createRoutesFromChildren,
                    matchRoutes,
                }),
                Sentry.browserProfilingIntegration(),
                sentryReplay,
            ],
            tracesSampler(samplingContext) {
                if (samplingContext.parentSampled) {
                    return samplingContext.parentSampled;
                }

                if (samplingContext.name.includes("healthcheck")) {
                    return 0;
                }

                return samplingContext.inheritOrSampleWith(mystiqueEnv === "prod" ? 0.001 : 0.1);
            },
            // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
            // tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],

            // Capture Replay for 10% of all sessions,
            // plus for 100% of sessions with an error
            replaysSessionSampleRate: 0,
            replaysOnErrorSampleRate: 1.0,

            // By default, Sentry SDKs normalize any context to a depth of 3.
            // You may want to increase this for sending Redux states.
            normalizeDepth: 10,
        });
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error initializing Sentry:", error);
    }
};
